import React from "react";
import { Alert } from "@mui/material";
import FormItem from "@components/FormItem/FormItem";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useApproveExemptedDialog } from "@features/bay-hire/hooks/useApproveExemptedDialog";
import {
  APPROVE_EXEMPTED_DIALOG_FIELDS as F,
  type ApproveExemptedDialogFormData,
} from "@features/bay-hire/schema/approveExemptedDialogSchema";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface ApproveExemptedDialogProps {
  open: boolean;
  requestId: string;
  /** Vendor-requested window; seeded silently, same as PreApproveDialog. */
  dateFrom?: string | null;
  dateTo?: string | null;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
}

/**
 * DFT-0084: EXEMPTED-only direct-approve dialog. Deliberately much smaller than
 * PreApproveDialog - no Chargeable/Payment type display (unambiguously not chargeable), no Total
 * Amount, no HOD upload (already vendor-supplied at creation, DFT-0044 D39/AC9), no supporting-
 * document upload. Just Remarks (optional) and the same two-step confirm pattern every other
 * Bay Hire Ops decision uses.
 */
const ApproveExemptedDialog: React.FC<ApproveExemptedDialogProps> = ({
  open,
  requestId,
  dateFrom,
  dateTo,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();

  const {
    form,
    submitError,
    isSubmitting,
    showConfirm,
    handleSubmitForm,
    handleConfirmSubmit,
    handleCancelConfirm,
  } = useApproveExemptedDialog({ open, requestId, dateFrom, dateTo, onSuccess });

  const { handleSubmit } = form;

  return (
    <div data-testid="approve-exempted-dialog">
      <SideDrawer
        isOpen={open}
        title={t("bay-hire.approve-exempted.title")}
        onClose={onClose}
        onSubmit={handleSubmit(handleSubmitForm)}
        isSubmitting={isSubmitting}
        submitLabel={t("bay-hire.approve-action")}
        cancelLabel={t("common.cancel", { defaultValue: "Cancel" })}
      >
        <div className="flex flex-col gap-1 px-6 py-2">
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="approve-exempted-submit-error">
              {submitError}
            </Alert>
          )}

          <FormProvider {...form}>
            <FormItem<ApproveExemptedDialogFormData, typeof F.PRE_APPROVAL_REMARKS>
              name={F.PRE_APPROVAL_REMARKS}
              type="textarea"
              label={t("bay-hire.pre-approve.preApprovalRemarks")}
              testId="approve-exempted-remarks"
              minRows={2}
            />
          </FormProvider>
        </div>
      </SideDrawer>
      <GenericDialog
        open={showConfirm}
        isInsideDrawer
        testId="approve-exempted-confirm-dialog"
        title={t("bay-hire.approve-exempted.confirm-title")}
        elementContent={t("bay-hire.approve-exempted.confirm-message")}
        cancelButtonText={t("common.cancel", { defaultValue: "Cancel" })}
        confirmButtonText={t("bay-hire.approve-action")}
        handleCloseDialog={handleCancelConfirm}
        confirmAction={() => void handleConfirmSubmit()}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default ApproveExemptedDialog;
